/**
 * Google Analytics (GA4) Integration Utility
 */

let isInitialized = false;

/**
 * Retrieves the Google Analytics measurement ID from environment variables.
 */
export const getGAMeasurementId = (): string | undefined => {
	const id = import.meta.env.VITE_GA_MEASUREMENT_ID;
	if (!id || id === 'G-XXXXXXXXXX' || id.trim() === '') {
		return undefined;
	}
	return id.trim();
};

/**
 * Initializes Google Analytics by injecting the gtag.js script into the document head
 * if a valid measurement ID is available.
 */
export const initGA = (measurementId?: string): boolean => {
	if (isInitialized) return true;
	if (typeof window === 'undefined') return false;

	const id = measurementId || getGAMeasurementId();
	if (!id) {
		console.debug('[GA] Google Analytics Measurement ID not configured. GA tracking disabled.');
		return false;
	}

	// Initialize dataLayer and gtag function
	window.dataLayer = window.dataLayer || [];
	if (!window.gtag) {
		window.gtag = function (...args: unknown[]) {
			window.dataLayer?.push(args as unknown as Record<string, unknown>);
		};
	}

	// Avoid duplicate script tag insertion
	const scriptId = 'google-analytics-gtag';
	if (!document.getElementById(scriptId)) {
		const script = document.createElement('script');
		script.id = scriptId;
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
		document.head.appendChild(script);
	}

	window.gtag('js', new Date());
	// Configure GA with send_page_view: false so SPA route handler controls page view events cleanly
	window.gtag('config', id, { send_page_view: false });

	isInitialized = true;
	console.debug(`[GA] Google Analytics initialized with ID: ${id}`);
	return true;
};

/**
 * Tracks a page view in Google Analytics.
 */
export const trackPageView = (path?: string, title?: string): void => {
	const id = getGAMeasurementId();
	const currentPath = path || `${window.location.pathname}${window.location.hash || ''}`;
	const currentTitle = title || document.title;

	if (window.gtag && id) {
		window.gtag('config', id, {
			page_path: currentPath,
			page_title: currentTitle,
		});
		window.gtag('event', 'page_view', {
			page_path: currentPath,
			page_title: currentTitle,
		});
	}
	console.debug(`[GA] Page View: ${currentPath} ("${currentTitle}")`);
};

export interface AnalyticsEventParams {
	category?: string;
	label?: string;
	value?: number;
	[key: string]: unknown;
}

/**
 * Tracks a custom event in Google Analytics.
 */
export const trackEvent = (
	action: string,
	params: AnalyticsEventParams = {}
): void => {
	const { category, label, value, ...extraParams } = params;

	if (window.gtag) {
		window.gtag('event', action, {
			event_category: category,
			event_label: label,
			value: value,
			...extraParams,
		});
	}
	console.debug(`[GA] Event: ${action}`, params);
};
